import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { DebouncedState, useDebouncedCallback } from 'use-debounce';
import { QueryKeys } from '~/api/queryKeys';
import { Companies } from '~/api/scheme';
import { CompanyEntityType } from './types';
import { DEBOUNCE_DELAY } from '../constants';

type Result = {
  companies: string[];
  debouncedCompaniesRefetch: DebouncedState<(name: string) => void>;
};

type Args = {
  companyType: CompanyEntityType;
};

const useRHFCompanySelect = (args: Args): Result => {
  const { companyType } = args;

  const [companies, setCompanies] = useState<string[]>([]);
  const [companyName, setCompanyName] = useState<string>('');

  useQuery(
    QueryKeys.companies(companyName),
    () => Companies.SearchResults.index({ name: companyName, type: companyType }),
    {
      onSuccess: (companiesResponse) => {
        const { data } = companiesResponse;
        if (isEmpty(data)) {
          setCompanies([]);
          return;
        }

        setCompanies(data.map((company) => company.name));
      },
      onError: () => {
        setCompanies([]);
      },
      enabled: companyName.length >= 3,
    }
  );

  useEffect(() => {
    if (companyName.length < 3) {
      setCompanies([]);
    }
  }, [companyName]);

  const debouncedCompaniesRefetch = useDebouncedCallback((name: string) => {
    setCompanyName(name);
  }, DEBOUNCE_DELAY);

  return {
    companies,
    debouncedCompaniesRefetch,
  };
};

export default useRHFCompanySelect;
